import { useTranslation } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { getUnauthenticatedLandingPath } from '@/core/constants/appConfig';
import {
  WEB_SHARE_IMAGE_HEIGHT,
  WEB_SHARE_IMAGE_TYPE,
  WEB_SHARE_IMAGE_URL,
  WEB_SHARE_IMAGE_WIDTH,
} from '@/core/constants/webSharePreview';
import {
  formatWebDocumentTitle,
  formatWebMetaDescription,
  normalizeWebPath,
  preferContentfulCopy,
  webDocumentTitleForPath,
} from '@/core/utils/formatWebDocumentTitle';
import {
  getWebDocumentTitleOverride,
  subscribeWebDocumentTitle,
} from '@/core/utils/webDocumentTitleOverride';
import Head from 'expo-router/head';
import { usePathname } from 'expo-router';
import React, { useSyncExternalStore } from 'react';
import type { WebDocumentHeadProps } from './WebDocumentHead';

/**
 * Sets the web document title through Expo Router's Head API.
 * Expo Router disables React Navigation's automatic document title, and the
 * static HTML shell otherwise ships an empty `<title>`.
 * One Head owns the tab. A focused screen can override it for its own path.
 */
export function WebDocumentHead({
  title,
  description,
  pathname,
}: WebDocumentHeadProps): React.ReactElement {
  const { t } = useTranslation();
  const { currentLanguage } = useI18nContext();
  const pageTitle = title ?? webDocumentTitleForPath(pathname, {
    welcomeTitle: preferContentfulCopy(
      t('webHome.documentWelcome'),
      'webHome.documentWelcome',
      t('welcome.welcomeTitle'),
    ),
    exploreBloomWorld: preferContentfulCopy(
      t('webHome.documentHome'),
      'webHome.documentHome',
      t('bloomUniversal.exploreTitle'),
    ),
    exploreProperties: preferContentfulCopy(
      t('webHome.documentProperties'),
      'webHome.documentProperties',
      t('guestWeb.exploreProperties'),
    ),
    projectDetails: preferContentfulCopy(
      t('webHome.documentProjectDetails'),
      'webHome.documentProjectDetails',
      t('guestWeb.unitDetails'),
    ),
    faq: preferContentfulCopy(
      t('webHome.documentFaq'),
      'webHome.documentFaq',
      t('profile.faq'),
    ),
    universalLanding: getUnauthenticatedLandingPath() === '/universal',
  });
  const appName = preferContentfulCopy(
    t('webHome.documentAppName'),
    'webHome.documentAppName',
    t('welcome.appName'),
  );
  const documentTitle = formatWebDocumentTitle(pageTitle, appName);
  const documentDescription = formatWebMetaDescription(
    description?.trim()
      ? description
      : preferContentfulCopy(
          t('webHome.documentDescription'),
          'webHome.documentDescription',
          t('welcome.heroSubtitle'),
        ),
  );

  return (
    <Head key={documentTitle}>
      <html lang={currentLanguage} />
      <title>{documentTitle}</title>
      {documentDescription.length > 0 ? (
        <meta name="description" content={documentDescription} />
      ) : null}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={appName} />
      <meta property="og:title" content={documentTitle} />
      {documentDescription.length > 0 ? (
        <meta property="og:description" content={documentDescription} />
      ) : null}
      <meta property="og:image" content={WEB_SHARE_IMAGE_URL} />
      <meta property="og:image:secure_url" content={WEB_SHARE_IMAGE_URL} />
      <meta property="og:image:type" content={WEB_SHARE_IMAGE_TYPE} />
      <meta property="og:image:width" content={WEB_SHARE_IMAGE_WIDTH} />
      <meta property="og:image:height" content={WEB_SHARE_IMAGE_HEIGHT} />
      <meta property="og:image:alt" content={appName} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={documentTitle} />
      {documentDescription.length > 0 ? (
        <meta name="twitter:description" content={documentDescription} />
      ) : null}
      <meta name="twitter:image" content={WEB_SHARE_IMAGE_URL} />
      <meta name="twitter:image:alt" content={appName} />
    </Head>
  );
}

/** One document title for the app. A focused screen can override the path title. */
export function WebPathDocumentTitle(): React.ReactElement {
  const pathname = usePathname();
  const override = useSyncExternalStore(
    subscribeWebDocumentTitle,
    getWebDocumentTitleOverride,
    () => null,
  );
  const overrideMatchesRoute =
    override != null &&
    normalizeWebPath(override.pathname) === normalizeWebPath(pathname);
  if (override && overrideMatchesRoute) {
    return (
      <WebDocumentHead
        pathname={pathname}
        title={override.title}
        description={override.description}
      />
    );
  }
  return <WebDocumentHead pathname={pathname} />;
}
